import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { PatchNote } from '../../../types/patchNote';
import { timeAgo } from '../../../utils/timeAgo';

const categoryAccent: Record<string, string> = {
  Warzone: 'bg-[#ff6b35]/20 text-[#ff6b35]',
  Multiplayer: 'bg-[#00ff41]/10 text-[#00ff41]',
  Zombies: 'bg-purple-900/40 text-purple-300',
};

interface PatchNoteListProps {
  onNew: () => void;
  onEdit: (patch: PatchNote) => void;
}

export default function PatchNoteList({ onNew, onEdit }: PatchNoteListProps) {
  const [patches, setPatches] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchPatches = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('patch_notes')
      .select('*')
      .order('date', { ascending: false });
    setPatches((data as PatchNote[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchPatches();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this patch note permanently?')) return;
    setDeletingId(id);
    await supabase.from('patch_notes').delete().eq('id', id);
    setPatches((prev) => prev.filter((p) => p.id !== id));
    setDeletingId(null);
    showToast('Patch note deleted.');
  };

  const handleTogglePublish = async (patch: PatchNote) => {
    const newState = !patch.published;
    await supabase.from('patch_notes').update({ published: newState }).eq('id', patch.id);
    setPatches((prev) =>
      prev.map((p) => (p.id === patch.id ? { ...p, published: newState } : p))
    );
    showToast(newState ? 'Patch note published!' : 'Moved to draft.');
  };

  const filtered = patches.filter((p) => {
    if (filter === 'published') return p.published;
    if (filter === 'draft') return !p.published;
    return true;
  });

  const publishedCount = patches.filter((p) => p.published).length;
  const draftCount = patches.filter((p) => !p.published).length;

  return (
    <>
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-[#00ff41] text-black text-sm font-['Barlow_Condensed'] font-bold px-5 py-3 rounded-md">
          {toast}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Total Patches', value: patches.length, icon: 'ri-file-list-3-line' },
          { label: 'Published', value: publishedCount, icon: 'ri-checkbox-circle-line', color: 'text-[#00ff41]' },
          { label: 'Drafts', value: draftCount, icon: 'ri-draft-line', color: 'text-[#ff6b35]' },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#111] border border-[#1f1f1f] rounded-lg p-5 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#0a0a0a] rounded-md">
              <i className={`${stat.icon} ${stat.color ?? 'text-gray-400'} text-xl`} />
            </div>
            <div>
              <div className={`font-['Barlow_Condensed'] font-black text-3xl leading-none ${stat.color ?? 'text-white'}`}>
                {stat.value}
              </div>
              <div className="text-gray-500 text-xs mt-1">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter + New button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {(['all', 'published', 'draft'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-['Barlow_Condensed'] font-bold text-sm tracking-widest px-4 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                filter === f
                  ? 'bg-[#00ff41] border-[#00ff41] text-black'
                  : 'border-[#2a2a2a] text-gray-400 hover:border-[#444] hover:text-white'
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-widest px-5 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-base" /> NEW PATCH NOTE
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-24 text-gray-600">
          <i className="ri-loader-4-line animate-spin text-3xl mb-3 block" />
          Loading patch notes...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#1f1f1f] rounded-lg">
          <i className="ri-file-list-3-line text-4xl text-gray-700 mb-3 block" />
          <p className="text-gray-600 text-sm mb-4">No patch notes yet.</p>
          <button
            onClick={onNew}
            className="bg-[#00ff41] text-black font-['Barlow_Condensed'] font-black text-sm tracking-wider px-5 py-2 rounded-md cursor-pointer whitespace-nowrap"
          >
            CREATE FIRST PATCH NOTE
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((patch) => (
            <div
              key={patch.id}
              className="bg-[#111] border border-[#1f1f1f] rounded-lg p-5 flex items-center gap-4 hover:border-[#2a2a2a] transition-colors"
            >
              {/* Version badge */}
              <div className="shrink-0 w-16 text-center">
                <div className={`font-['Barlow_Condensed'] font-black text-2xl leading-none ${
                  patch.category === 'Warzone' ? 'text-[#ff6b35]' :
                  patch.category === 'Multiplayer' ? 'text-[#00ff41]' : 'text-purple-400'
                }`}>
                  {patch.version}
                </div>
              </div>

              {/* Divider */}
              <div className="w-px self-stretch bg-[#1e1e1e] shrink-0" />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs font-['Barlow_Condensed'] font-bold tracking-wider px-2 py-0.5 rounded ${categoryAccent[patch.category] ?? 'bg-gray-800 text-gray-400'}`}>
                    {patch.category.toUpperCase()}
                  </span>
                  <span className={`text-xs font-['Barlow_Condensed'] font-bold px-2 py-0.5 rounded ${patch.published ? 'bg-[#00ff41]/10 text-[#00ff41]' : 'bg-[#2a2a2a] text-gray-500'}`}>
                    {patch.published ? 'LIVE' : 'DRAFT'}
                  </span>
                  <span className="text-[#444] text-xs font-['Barlow_Condensed']">
                    {timeAgo(patch.date)}
                  </span>
                </div>
                <h3 className="text-white text-sm font-medium truncate">{patch.title}</h3>
                <p className="text-[#555] text-xs mt-0.5 truncate">{patch.highlight}</p>
              </div>

              {/* Sections count */}
              <div className="shrink-0 text-center hidden md:block">
                <div className="text-white font-['Barlow_Condensed'] font-black text-xl leading-none">
                  {patch.sections.length}
                </div>
                <div className="text-[#444] text-xs">sections</div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleTogglePublish(patch)}
                  className={`text-xs font-['Barlow_Condensed'] font-bold px-3 py-1.5 rounded border transition-all cursor-pointer whitespace-nowrap ${
                    patch.published
                      ? 'border-[#2a2a2a] text-gray-400 hover:text-[#ff6b35] hover:border-[#ff6b35]/40'
                      : 'border-[#00ff41]/30 text-[#00ff41] hover:bg-[#00ff41]/10'
                  }`}
                >
                  {patch.published ? 'UNPUBLISH' : 'PUBLISH'}
                </button>
                <button
                  onClick={() => onEdit(patch)}
                  className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#444] rounded transition-all cursor-pointer"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button
                  onClick={() => handleDelete(patch.id)}
                  disabled={deletingId === patch.id}
                  className="w-8 h-8 flex items-center justify-center border border-[#2a2a2a] text-gray-400 hover:text-red-400 hover:border-red-800/40 rounded transition-all cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
